import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import './HorizontalToolbarToggler.css';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { map as mapConfig, explorer } from '../../_config/map';
import BaseLayer from '../BaseLayer/';
import GridLayer from '../GridLayer/';
import LocateMe from '../LocateMe';
import LocateAddress from '../LocateAddress';
import FileUploader from '../FileUploader/';
import StatisticGraphics from '../StatisticGraphics';
import StoryTelling from '../StoryTelling/StoryTelling';
import HintWindow from '../HintWindow/';
import PrintWindow from '../PrintWindow';
import ShareWindow from '../ShareWindow';
import FeatureInfo from '../FeatureInfo';
import DistanceMeasurer from '../DistanceMeasurer';
import AreaMeasurer from '../AreaMeasurer';
import DownloadDadosModal from '../Toolbar/components/download/download';
import LayerSwitcher from '../LayerSwitcher/LayerSwitcher';
import IAChatDialog from '../Verdian/verdian';
import VerdianIcon from '../../_assets/img/verdian-icon.png'

const DATA_EXPLORER = {
    id: 'DataExplorer',
    icon: 'fa-solid fa-layer-group',
    labelKey: 'toolbar.dataExplorer'
};



const TOOLS = [
    {
        id: 'FeatureInfo',
        icon: 'fa-solid fa-circle-info',
        labelKey: 'toolbar.identifyFeature'
    },
    {
        id: 'DistanceMeasurer',
        icon: 'fa-solid fa-ruler-horizontal',
        labelKey: 'toolbar.measureDistance'
    },
    {
        id: 'AreaMeasurer',
        icon: 'fa-solid fa-draw-polygon',
        labelKey: 'toolbar.measureArea'
    },
    {
        id: 'BaseLayer',
        icon: 'fa-solid fa-map',
        labelKey: 'toolbar.baseLayer'
    },
    {
        id: 'GridLayer',
        icon: 'fa-solid fa-th-large',
        labelKey: 'toolbar.gridLayer'
    },
    {
        id: 'LocateMe',
        icon: 'fa-solid fa-location-crosshairs',
        labelKey: 'toolbar.myLocation'
    },
    {
        id: 'LocateAddress',
        icon: 'fa-solid fa-map-marker',
        labelKey: 'toolbar.locateAddress'
    },
    {
        id: 'FileUploader',
        icon: 'fa-solid fa-upload',
        labelKey: 'toolbar.importFile'
    },
    {
        id: 'StatisticGraphics',
        icon: 'fa-solid fa-chart-bar',
        labelKey: 'toolbar.statisticGraphics'
    },
    {
        id: 'StoryTelling',
        icon: 'fa-solid fa-book',
        labelKey: 'toolbar.storytelling'
    },
    {
        id: 'HintWindow',
        icon: 'fa-solid fa-info-circle',
        labelKey: 'toolbar.hints'
    },
    {
        id: 'PrintWindow',
        icon: 'fa-solid fa-print',
        labelKey: 'toolbar.print'
    },
    {
        id: 'DownloadDados',
        icon: 'fa-solid fa-download',
        labelKey: 'toolbar.downloadData'
    },
    {
        id: 'ShareWindow',
        icon: 'fa-solid fa-share-nodes',
        labelKey: 'toolbar.share'
    },
    {
        id: 'PortalAdministrativo',
        icon: 'fa-solid fa-building-columns',
        labelKey: 'toolbar.adminPortal'
    },

];

const EXCLUSIVE_TOOLS = ['FeatureInfo', 'DistanceMeasurer', 'AreaMeasurer'];

function HorizontalToolbarToggler({
    map,
    baseLayerRef,
    onBaseLayerChange
}) {
    const { t } = useTranslation();
    const element = useRef();
    const [active, setActive] = useState(true);
    const [activeTool, setActiveTool] = useState(null);
    const [activeToolId, setActiveToolId] = useState(null);
    const [selectedTool, setSelectedTool] = useState(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [dataExplorerOpen, setDataExplorerOpen] = useState(false);

    const featureInfoRef = useRef(null);
    const distanceMeasurerRef = useRef(null);
    const areaMeasurerRef = useRef(null);

    const handleClick = () => {
        setActive(prev => !prev);
    };

    const handleDataExplorerToggle = () => {
        if (!active) {
            return;
        }

        setDataExplorerOpen(prev => {
            const nextState = !prev;

            setSelectedTool(
                nextState ? DATA_EXPLORER.id : null
            );

            return nextState;
        });
    };

    const handleDataExplorerClose = () => {
        setDataExplorerOpen(false);

        if (selectedTool === DATA_EXPLORER.id) {
            setSelectedTool(null);
        }
    };

    const handleActiveTool = (instance, toolId) => {
        if (instance) {
            setActiveTool(instance);
            setActiveToolId(toolId);
            setSelectedTool(toolId);
            return;
        }

        setActiveToolId(currentToolId => {
            if (currentToolId === toolId) {
                setActiveTool(null);
                setSelectedTool(currentSelectedTool =>
                    currentSelectedTool === toolId
                        ? null
                        : currentSelectedTool
                );

                return null;
            }

            return currentToolId;
        });
    };

    const deactivateAllExclusiveTools = (exceptToolId = null) => {
        const tools = [
            {
                ref: featureInfoRef,
                id: 'FeatureInfo'
            },
            {
                ref: distanceMeasurerRef,
                id: 'DistanceMeasurer'
            },
            {
                ref: areaMeasurerRef,
                id: 'AreaMeasurer'
            }
        ];

        tools.forEach(({ ref, id }) => {
            if (
                id !== exceptToolId &&
                ref.current &&
                typeof ref.current.deactivate === 'function'
            ) {
                ref.current.deactivate();
            }
        });
    };

    const handleToolClick = toolId => {
        if (toolId === DATA_EXPLORER.id) {
            handleDataExplorerToggle();
            return;
        }

        if (toolId === 'PortalAdministrativo') {
            window.open(
                'https://hgpedea.sema.ce.gov.br/gestor',
                '_blank',
                'noopener,noreferrer'
            );
            return;
        }

        if (toolId === 'DownloadDados') {
            setSelectedTool('DownloadDados');
            setShowDownloadModal(true);
            return;
        }

        const isExclusive = EXCLUSIVE_TOOLS.includes(toolId);

        const refMap = {
            FeatureInfo: featureInfoRef,
            DistanceMeasurer: distanceMeasurerRef,
            AreaMeasurer: areaMeasurerRef
        };

        if (isExclusive && activeToolId === toolId) {
            const currentRef = refMap[toolId];

            if (
                currentRef?.current &&
                typeof currentRef.current.deactivate === 'function'
            ) {
                currentRef.current.deactivate();
            }

            setActiveTool(null);
            setActiveToolId(null);
            setSelectedTool(null);

            return;
        }

        if (isExclusive) {
            deactivateAllExclusiveTools(toolId);
            setSelectedTool(toolId);
            setActiveToolId(toolId);
        } else {
            setSelectedTool(toolId);
        }

        const toolMap = {
            FeatureInfo: '.FeatureInfoButton',
            DistanceMeasurer: '.DistanceMeasurerButton',
            AreaMeasurer: '.AreaMeasurerButton'
        };

        if (toolMap[toolId]) {
            const button = document.querySelector(toolMap[toolId]);

            if (button) {
                button.click();
            }

            return;
        }

        const container = document.getElementById(toolId);
        const button = container?.querySelector('button');

        button?.click();
    };

    const isToolActive = toolId => {
        if (toolId === DATA_EXPLORER.id) {
            return dataExplorerOpen;
        }

        if (EXCLUSIVE_TOOLS.includes(toolId)) {
            return activeToolId === toolId;
        }

        if (toolId === 'DownloadDados') {
            return showDownloadModal;
        }

        return selectedTool === toolId;
    };

    const isAnyExclusiveToolActive = () => {
        return activeToolId !== null;
    };

    const isToolDisabled = toolId => {
        if (!EXCLUSIVE_TOOLS.includes(toolId)) {
            return false;
        }

        if (!isAnyExclusiveToolActive()) {
            return false;
        }

        return activeToolId !== toolId;
    };

    return (
        <>
            <div
                className={`
                    HorizontalToolbarToggler
                    ${active ? 'is-open' : ''}
                `}
            >
                <Tooltip
                    target="
                        .HorizontalToolbarToggler__tab--tooltip,
                        .HorizontalToolbarToggler__item--tooltip,
                        .DataExplorerPanel__tab--tooltip,
                        .HorizontalToolbarToggler__gestor
                    "
                    position="right"
                    showDelay={150}
                />

                <Button
                    className="
                        HorizontalToolbarToggler__tab
                        HorizontalToolbarToggler__tab--tooltip
                    "
                    type="button"
                    aria-label={
                        active
                            ? t('toolbar.closeMenu')
                            : t('toolbar.openMenu')
                    }
                    data-pr-tooltip={
                        active
                            ? t('toolbar.closeMenu')
                            : t('toolbar.openMenu')
                    }
                    ref={element}
                    onClick={handleClick}
                >
                    <i
                        className={`
                            fa-solid
                            ${active ? 'fa-chevron-left' : 'fa-chevron-right'}
                        `}
                        aria-hidden="true"
                    />
                </Button>

                <div className="HorizontalToolbarToggler__panel">
                    <div className="HorizontalToolbarToggler__list">
                        <div className="HorizontalToolbarToggler__section">
                            <div
                                className={`
                                    HorizontalToolbarToggler__item
                                    HorizontalToolbarToggler__item--dataExplorer
                                    ${isToolActive(DATA_EXPLORER.id)
                                        ? 'HorizontalToolbarToggler__item--active'
                                        : ''
                                    }
                                    ${!active
                                        ? 'HorizontalToolbarToggler__item--tooltip'
                                        : ''
                                    }
                                `}
                                onClick={() =>
                                    handleToolClick(DATA_EXPLORER.id)
                                }
                                data-pr-tooltip={
                                    !active
                                        ? t(DATA_EXPLORER.labelKey)
                                        : undefined
                                }
                                aria-label={t(DATA_EXPLORER.labelKey)}
                            >
                                <i
                                    className={DATA_EXPLORER.icon}
                                    aria-hidden="true"
                                />

                                <span className="HorizontalToolbarToggler__itemLabel">
                                    {t(DATA_EXPLORER.labelKey)}
                                </span>

                                <i
                                    className={`
                                        fa-solid
                                        ${dataExplorerOpen
                                            ? 'fa-chevron-right'
                                            : 'fa-chevron-left'
                                        }
                                        HorizontalToolbarToggler__dataExplorerArrow
                                    `}
                                    aria-hidden="true"
                                />
                            </div>

                   
                        </div>

                        <div className="HorizontalToolbarToggler__divider">
                            <i
                                className="fa-solid fa-screwdriver-wrench"
                                aria-hidden="true"
                            />
                            <span>{t('toolbar.tools')}</span>
                        </div>

                        <div className="HorizontalToolbarToggler__tools">
                            {TOOLS.map((tool, index) => {
                                const toolActive = isToolActive(tool.id);
                                const toolDisabled = isToolDisabled(tool.id);

                                return (
                                    <div
                                        key={tool.id}
                                        className={`
                                            HorizontalToolbarToggler__item
                                            ${toolActive
                                                ? 'HorizontalToolbarToggler__item--active'
                                                : ''
                                            }
                                            ${toolDisabled
                                                ? 'HorizontalToolbarToggler__item--disabled'
                                                : ''
                                            }
                                            ${!active
                                                ? 'HorizontalToolbarToggler__item--tooltip'
                                                : ''
                                            }
                                        `}
                                        style={{
                                            '--item-index': index
                                        }}
                                        onClick={() =>
                                            handleToolClick(tool.id)
                                        }
                                        data-pr-tooltip={
                                            !active
                                                ? t(tool.labelKey)
                                                : undefined
                                        }
                                        aria-label={t(tool.labelKey)}
                                    >
                                        <i
                                            className={tool.icon}
                                            aria-hidden="true"
                                        />

                                        <span className="HorizontalToolbarToggler__itemLabel">
                                            {t(tool.labelKey)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="HorizontalToolbarToggler__functionalTools">
                    <FeatureInfo
                        ref={featureInfoRef}
                        map={map}
                        handleActiveTool={instance =>
                            handleActiveTool(instance, 'FeatureInfo')
                        }
                        activeTool={activeTool}
                    />

                    <DistanceMeasurer
                        ref={distanceMeasurerRef}
                        map={map}
                        handleActiveTool={instance =>
                            handleActiveTool(instance, 'DistanceMeasurer')
                        }
                        activeTool={activeTool}
                    />

                    <AreaMeasurer
                        ref={areaMeasurerRef}
                        map={map}
                        handleActiveTool={instance =>
                            handleActiveTool(instance, 'AreaMeasurer')
                        }
                        activeTool={activeTool}
                    />
                </div>

                <BaseLayer
                    map={map}
                    ref={baseLayerRef}
                    onBaseLayerChange={onBaseLayerChange}
                />

                <GridLayer map={map} />
                <LocateMe map={map} />
                <LocateAddress map={map} />
                <FileUploader map={map} />
                <StatisticGraphics map={map} />
                <StoryTelling map={map} />
                <HintWindow map={map} />
                <PrintWindow map={map} />
                <ShareWindow map={map} />

                <div id="IAChat">
                    <IAChatDialog />
                </div>

                <DownloadDadosModal
                    active={showDownloadModal}
                    close={() => {
                        setShowDownloadModal(false);
                        setSelectedTool(null);
                    }}
                    title={t('toolbar.downloadData')}
                />
            </div>

            {dataExplorerOpen && (
                <LayerSwitcher
                    map={map || mapConfig}
                    explorer={explorer}
                    currentLanguage="pt"
                    visible={dataExplorerOpen}
                    onClose={handleDataExplorerClose}
                    hasNewVisibleLayer={() => { }}
                    activeVisible={true}
                />
            )}
        </>
    );
}

export default HorizontalToolbarToggler;